import React, {memo, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import Grid from '@mui/material/Grid';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import parse from 'html-react-parser';
import {colors, gradients, fontWeight, fontFamily, fontSize, lineHeight, spacing, borderRadius, shadows, typography} from '@/styles/tokens';

// ─── Types ──────────────────────────────────────────────────────────────────────

export interface FAQItem {
    q: string;
    a: string;
}

export interface FAQCategory {
    category?: string;
    questions: FAQItem[];
}

interface FAQProps {
    heading?: string;
    faqData: FAQCategory[];
}

// ─── sx constants ────────────────────────────────────────────────────────────────

const accordionSx = {
    boxShadow: 'none',
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: `${borderRadius.sm} !important`,
    mb: 0,
    fontFamily: fontFamily.body,
    overflow: 'hidden',
    '&:before': {
        display: 'none'
    },
    '&.Mui-expanded': {
        m: 0,
        boxShadow: shadows.lg,
        border: `1px solid ${colors.neutral.borderLight}`
    }
} as const;

const accordionSummarySx = {
    px: spacing[24],
    py: spacing[12],
    '& .MuiAccordionSummary-expandIconWrapper': {
        color: colors.text.primary
    },
    '& .MuiAccordionSummary-content': {
        fontWeight: fontWeight.semibold,
        fontSize: fontSize[16],
        color: colors.text.primary,
        lineHeight: lineHeight[24],
        fontFamily: fontFamily.body,
        my: spacing[8]
    }
} as const;

const expandedSummarySx = (isExpanded: boolean) => ({
    ...accordionSummarySx,
    background: isExpanded ? gradients.primaryActive : colors.background.subtle,
    borderBottom: isExpanded ? 'none' : `1px solid ${colors.neutral.borderLight}`,
    // No maxHeight: a capped header clipped questions that wrap to three lines on phones.
    minHeight: `${spacing[60]} !important`,
    display: 'flex',
    alignItems: 'center',
    '& .MuiAccordionSummary-content': {
        ...accordionSummarySx['& .MuiAccordionSummary-content'],
        color: isExpanded ? colors.text.inverse : colors.text.primary
    }
});

const headingSx = {
    ...typography.sectionHeading,
    textAlign: 'center',
    mb: spacing[24]
} as const;

const detailsSx = {
    px: spacing[24],
    pt: spacing[24],
    pb: spacing[24],
    bgcolor: colors.background.default
} as const;

const answerSx = {
    fontSize: fontSize[16],
    lineHeight: lineHeight[28],
    color: colors.text.primary,
    whiteSpace: 'pre-line' as const
} as const;

const questionTextSx = {
    fontWeight: fontWeight.semibold,
    fontSize: fontSize[16],
    lineHeight: lineHeight[24],
    m: 0
} as const;

// ─── Sub-component ───────────────────────────────────────────────────────────────

const FAQAccordion = ({
    item,
    idx,
    expanded,
    onToggle
}: {
    item: FAQItem;
    idx: number;
    expanded: number | false;
    onToggle: (idx: number) => (_: React.SyntheticEvent, isExpanded: boolean) => void;
}) => {
    const isExpanded = expanded === idx;
    return (
        <Box mb={{xs: spacing[16], md: spacing[24]}}>
            <Accordion className="font-headers" sx={accordionSx} expanded={isExpanded} onChange={onToggle(idx)}>
                <AccordionSummary
                    expandIcon={
                        isExpanded ? <RemoveIcon sx={{color: colors.text.inverse}} /> : <AddIcon sx={{color: colors.text.primary}} />
                    }
                    sx={expandedSummarySx(isExpanded)}
                >
                    <Typography component="h3" sx={questionTextSx}>
                        {item.q}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={detailsSx}>
                    <Typography className="font-regular-text remove-underline" variant="subtitle1" component="p" sx={answerSx}>
                        {parse(item.a)}
                    </Typography>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

// ─── Main component ──────────────────────────────────────────────────────────────

const FAQ = ({heading, faqData}: FAQProps) => {
    const allQuestions = faqData?.flatMap((category) => category.questions) || [];
    const [expanded, setExpanded] = useState<number | false>(false);

    const handleChange = (panel: number) => (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    const half = Math.ceil(allQuestions.length / 2);
    const leftCol = allQuestions.slice(0, half);
    const rightCol = allQuestions.slice(half);

    return (
        <Box>
            <MUIContainer>
                {heading && (
                    <Typography textAlign="center" variant="h1" component="h2" className="font-headers" sx={headingSx}>
                        {parse(heading)}
                    </Typography>
                )}

                <Grid container spacing={{xs: spacing[16], md: spacing[24]}}>
                    <Grid item xs={12} md={6}>
                        {leftCol.map((item, idx) => (
                            <FAQAccordion key={idx} item={item} idx={idx} expanded={expanded} onToggle={handleChange} />
                        ))}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {rightCol.map((item, idx) => {
                            const actualIdx = idx + half;
                            return <FAQAccordion key={actualIdx} item={item} idx={actualIdx} expanded={expanded} onToggle={handleChange} />;
                        })}
                    </Grid>
                </Grid>
            </MUIContainer>
        </Box>
    );
};

export default memo(FAQ);
